import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  BookOpen,
  MessageSquare,
  Search,
  Plus,
  ThumbsUp,
  Tag,
  Lock,
  ArrowLeft,
  CheckCircle,
  XCircle,
  HelpCircle,
  Users,
  Sparkles,
} from 'lucide-react'
import Card from '../components/Card'

const ForumHelp = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState(null)

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Welcome to the NaturAssist Forum! This is a community space where practitioners can ask questions, share knowledge, and get help.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Quick Start:</h4>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Browse topics by clicking on category cards</li>
              <li>Use the search bar to find specific discussions</li>
              <li>Click "New Topic" to start a discussion</li>
              <li>Click on any topic to read and reply</li>
            </ol>
          </div>
        </div>
      ),
    },
    {
      id: 'access',
      title: 'Access Requirements',
      icon: Lock,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            The forum is available to all paid subscribers, trial users, and demo organizations.
          </p>
          <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">Category Access by Tier:</h4>
            <ul className="space-y-2 text-sm text-primary-800 dark:text-primary-200">
              <li className="flex items-center gap-2">
                <CheckCircle size={16} />
                <span><strong>Essential+:</strong> General Discussion, Best Practices, Community Support</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={16} />
                <span><strong>Professional+:</strong> All Essential categories + Feature Requests</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={16} />
                <span><strong>Enterprise:</strong> All categories including Advanced Topics</span>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'creating-topics',
      title: 'Creating Topics',
      icon: Plus,
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Steps to Create a Topic:</h4>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Click the "New Topic" button (top right)</li>
              <li>Select an appropriate category</li>
              <li>Enter a clear, descriptive title</li>
              <li>Write your question or discussion content</li>
              <li>Click "Create Topic"</li>
            </ol>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">Tips for Good Topics:</h4>
            <ul className="space-y-1 text-sm text-green-800 dark:text-green-200">
              <li>✅ Use descriptive titles</li>
              <li>✅ Provide context and background</li>
              <li>✅ Ask specific questions</li>
              <li>✅ Search first to avoid duplicates</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'replying',
      title: 'Replying to Posts',
      icon: MessageSquare,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            To reply to a topic, open the topic and scroll to the bottom. Enter your reply in the text area and click "Post Reply".
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Reactions:</h4>
            <p className="text-gray-700 dark:text-gray-300">
              Click the thumbs-up icon to like a post. Click again to remove your like. This helps identify helpful content.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'searching',
      title: 'Searching and Filtering',
      icon: Search,
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Search:</h4>
            <p className="text-gray-700 dark:text-gray-300">
              Use the search bar at the top to search across all topics, content, tags, and categories. The search is real-time and updates as you type.
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Filter by Category:</h4>
            <p className="text-gray-700 dark:text-gray-300">
              Click on any category card to filter topics. Click again to clear the filter.
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Filter by Tags:</h4>
            <p className="text-gray-700 dark:text-gray-300">
              Click on any tag below a topic title to filter by that topic. Click the "×" on active filters to clear them.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'categories',
      title: 'Categories',
      icon: Tag,
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-gray-900 dark:text-white">General Discussion</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Essential+ • General questions and discussions</p>
            </div>
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-gray-900 dark:text-white">Best Practices</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Essential+ • Share tips and workflows</p>
            </div>
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-gray-900 dark:text-white">Community Support</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Essential+ • Help other practitioners</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-gray-900 dark:text-white">Feature Requests</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Professional+ • Suggest new features</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold text-gray-900 dark:text-white">Advanced Topics</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Growth+ • Technical discussions</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Creating Quality Topics:</h4>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
              <li>✅ Use clear, descriptive titles</li>
              <li>✅ Provide context and background</li>
              <li>✅ Be specific about your question</li>
              <li>✅ Search first to avoid duplicates</li>
            </ul>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">Writing Helpful Replies:</h4>
            <ul className="space-y-1 text-sm text-green-800 dark:text-green-200">
              <li>✅ Be constructive and helpful</li>
              <li>✅ Share your experience</li>
              <li>✅ Stay on topic</li>
              <li>✅ Thank people who help</li>
            </ul>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">Community Guidelines:</h4>
            <ul className="space-y-1 text-sm text-red-800 dark:text-red-200">
              <li>❌ No spam or advertisements</li>
              <li>❌ No personal attacks</li>
              <li>❌ No duplicate posts</li>
              <li>❌ Be respectful and professional</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: HelpCircle,
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Can't Access Forum?</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Ensure you're on a paid plan, trial, or demo. Check your subscription status in Organization Settings.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Can't See Categories?</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Some categories require Professional or Enterprise tier. Upgrade your subscription to access all categories.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Search Not Working?</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Try different keywords, clear your search, or refresh the page. Check your internet connection.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Need More Help?</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Post in the Community Support category or contact support through the NaturAssist Support page.
              </p>
            </div>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-4xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl my-8"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Forum Help Guide</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Learn how to use the NaturAssist Forum</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close help"
              >
                <XCircle className="w-6 h-6 text-gray-500 dark:text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {sections.map((section) => {
              const Icon = section.icon
              const isActive = activeSection === section.id
              return (
                <motion.button
                  key={section.id}
                  onClick={() => setActiveSection(isActive ? null : section.id)}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    isActive
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {section.title}
                      </h3>
                      {isActive && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-3 text-sm text-gray-700 dark:text-gray-300"
                        >
                          {section.content}
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.button>
              )
            })}
          </div>

          {/* Quick Links */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Quick Links</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <a
                href="https://naturassist.cwickcraft.com/support"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <Users size={20} className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">Contact Support</span>
              </a>
              <Link
                to="/organization"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <Lock size={20} className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">Check Subscription</span>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default ForumHelp
